/**
 * Guard điều hướng — thay `src/middleware.ts` của Next.
 *
 * Middleware cũ chạy ở edge và chỉ kiểm tra **sự tồn tại** của cookie `vgt.rt`
 * trước khi trang được render. SPA không có tầng đó, và JS cũng không đọc được
 * cookie `HttpOnly`, nên guard phải dựa vào kết quả bootstrap của `AuthProvider`
 * (nó gọi `/auth/session/refresh` một lần khi khởi động).
 *
 * Khác biệt hành vi phải chấp nhận — đã thống nhất khi duyệt kế hoạch:
 * bản Next chuyển hướng TRƯỚC khi tải trang, bản SPA phải tải app rồi mới biết
 * có phiên hay không, nên người chưa đăng nhập mở thẳng `/dashboard` sẽ thấy
 * một khoảnh khắc chờ trước khi bị đưa về `/login`.
 *
 * Đây CHỈ là điều hướng và trải nghiệm. Nó không bảo vệ dữ liệu: mọi endpoint
 * phía NestJS vẫn tự kiểm tra quyền, và người dùng tự ý vào được khung trang
 * cũng không đọc được gì nếu API từ chối.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useAuth } from '@/lib/auth-context';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Chưa biết có phiên hay không. Không được quyết vội: render `<Navigate>` ở
  // đây sẽ đá văng cả người đang có phiên hợp lệ, vì lần refresh đầu chưa kịp
  // trả lời.
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
          role="status"
          aria-label="Đang kiểm tra phiên đăng nhập"
        />
      </div>
    );
  }

  if (!user) {
    // Giữ nguyên định dạng `?redirect=` của middleware cũ, gồm cả query string,
    // để `pickRedirectTarget()` ở form login dùng lại được nguyên vẹn — kể cả
    // phần chống open-redirect đã có 16 test phủ.
    const target = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(target)}`} replace />;
  }

  return <>{children}</>;
}
